import {
    ChangeReadStrategy,
    CopyJobActivity,
    NullWatermarkBehavior,
    ReadMethod
} from 'app/model/copyjob/copy-job.activity';
import { copyActivityAdapts, CopyActivityAsyncAdapter } from 'app/model/adapters/activities/copy-activity.adapter';
import { AdapterRegistry, IModel, Metamodel, stringIsNullOrEmpty } from 'shell-models-lib';
import { Model } from 'app/model/backendmodel';
import { cloneDeep, pick } from 'lodash';
import { CopyJobActivityContext } from 'app/interfaces/copilot.copyjob.type';
import { ICopyActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { GlobalVariables } from 'app/services/globals';
import { isDeltaCloneCopy } from 'app/authoring/copy-job/wizard/copy-job-delta.util';

const copyJobActivityAdapts = {
    ...copyActivityAdapts,
    typeProperties: {
        ...copyActivityAdapts.typeProperties,
        readMethod: '*',
        columns: '*',
        captureInstanceName: GlobalVariables.copyJobCustomCaptureInstance ? '*' : undefined,
        nullWatermarkBehavior: GlobalVariables.copyJobNullRecords ? '*' : undefined,
        changeReadStrategy: GlobalVariables.copyJobRefreshWithoutIncrementalColumn ? '*' : undefined
    }
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(CopyJobActivity),
    asynchronous: true,
    adapts: copyJobActivityAdapts
})
export class CopyJobActivityAsyncAdapter extends CopyActivityAsyncAdapter {
    public override model(): IModel {
        return Metamodel.modelFor(CopyJobActivity);
    }

    public override async toFabricCopilotContext(activity: CopyJobActivity): Promise<ICopyActivityContext & CopyJobActivityContext> {
        // TODO enrich with source/target info
        const activityContext: ICopyActivityContext & CopyJobActivityContext = {
            Source: undefined,
            Target: undefined,

            // inherited mandatory fields from CopyActivity
            Name: undefined,
            Type: undefined,
            Dependencies: undefined
        };

        return activityContext;
    }

    protected override async _toResource(activity: CopyJobActivity): Promise<Model.CopyJobActivity> {
        const resource: Model.CopyJobActivity = (await super._toResource(activity)) as Model.CopyJobActivity;
        resource.typeProperties.readMethod = activity.readMethod;
        // super._toResource rebuilds typeProperties from scratch, so wildcard-mapped
        // fields must be re-applied here.
        if (GlobalVariables.copyJobRefreshWithoutIncrementalColumn && activity.changeReadStrategy != null) {
            resource.typeProperties.changeReadStrategy = activity.changeReadStrategy;
        }
        if (GlobalVariables.copyJobNullRecords && activity.nullWatermarkBehavior != null) {
            resource.typeProperties.nullWatermarkBehavior = activity.nullWatermarkBehavior;
        }
        if (
            !activity.isNativeCDCEnabled &&
            activity.readMethod === 'SnapshotPlusIncremental' &&
            activity.columns.length
        ) {
            resource.typeProperties.columns = activity.columns?.map((column) =>
                pick(column, ['name', 'type', 'physicalType', 'conversionSettings'])
            );
        }
        if (
            GlobalVariables.copyJobCustomCaptureInstance &&
            activity.isNativeCDCEnabled &&
            activity.readMethod === ReadMethod.SnapshotPlusIncremental &&
            !stringIsNullOrEmpty(activity.captureInstanceName)
        ) {
            resource.typeProperties.captureInstanceName = activity.captureInstanceName;
        }
        if (!isDeltaCloneCopy(activity) && activity.lowPrecisionDatatypeMode != null) {
            if (!resource.typeProperties.translator) {
                resource.typeProperties.translator = { type: 'TabularTranslator' };
            }
            if (!resource.typeProperties.translator.typeConversionSettings) {
                resource.typeProperties.translator.typeConversionSettings = {};
            }
            resource.typeProperties.translator.typeConversionSettings.lowPrecisionDatatypeMode = activity.lowPrecisionDatatypeMode;
        }
        return resource;
    }

    protected override async _toModel(modelActivity: Model.CopyJobActivity): Promise<CopyJobActivity> {
        const model: CopyJobActivity = (await super._toModel(modelActivity)) as CopyJobActivity;
        model.readMethod = modelActivity.typeProperties.readMethod as ReadMethod;
        if (GlobalVariables.copyJobNullRecords) {
            model.nullWatermarkBehavior = modelActivity.typeProperties.nullWatermarkBehavior as NullWatermarkBehavior;
        }
        if (
            GlobalVariables.copyJobRefreshWithoutIncrementalColumn &&
            modelActivity.typeProperties.changeReadStrategy != null
        ) {
            model.changeReadStrategy = modelActivity.typeProperties.changeReadStrategy as ChangeReadStrategy;
        }
        if (modelActivity.typeProperties.columns?.length) {
            model.columns = cloneDeep(modelActivity.typeProperties.columns);
        } else if (
            model.readMethod === ReadMethod.SnapshotPlusIncremental &&
            model.changeReadStrategy !== ChangeReadStrategy.Batch
        ) {
            // A full-copy table has no incremental columns and the default readMethod, but it
            // is not native (log-based) CDC; only treat empty columns as native CDC otherwise.
            model.isNativeCDCEnabled = true;
        }
        if (GlobalVariables.copyJobCustomCaptureInstance && model.isNativeCDCEnabled) {
            model.captureInstanceName = modelActivity.typeProperties.captureInstanceName;
        }
        if (modelActivity.typeProperties.translator?.typeConversionSettings?.lowPrecisionDatatypeMode != null) {
            model.lowPrecisionDatatypeMode = modelActivity.typeProperties.translator.typeConversionSettings.lowPrecisionDatatypeMode;
        }
        return model;
    }
}
